import { getLocalToday } from "utils/Common/Date";
import { useMemo, useState, useEffect } from "react";
import {
  DrawData,
  GcProgressItem,
  InspectionFormPayload,
  InspectorForm,
  Metrics,
  Photo,
  ResidentialFiItemPayload,
  ResidentialInspectionPayload,
  ResidentialLineItem,
  ResidentialPhase,
  SovItem,
  Project,
} from "../types/inspector.types";
import useInspectionCommercialForm from "../../../../hooks/useInspectionCommercialForm";
import useInspectionResidentialForm from "../../../../hooks/useInspectionResidentialForm";
import useInspectorProjects from "../../../../hooks/useInspectorProjects";
import {
  saveInspectionForm,
  saveResidentialInspectionForm,
} from "../../../../services/inspectionFormService";
import { getErrorMessage } from "../../../../utils/api/errorUtils";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import CommercialInspectorPage from "./CommercialInspectorPage";
import ResidentialInspectorPage from "./ResidentialInspectorPage";
import NoInspectionState from "./NoInspectionState";
import TopBar from "../components/TopBar";

const EMPTY_FORM: InspectorForm = {
  name: "",
  number: "",
  date: "",
  estimate: "",
  bank: "",
};

const EMPTY_DRAW_DATA: DrawData = {
  applicationNumber: "",
  projectName: "",
  period: "",
  currentPaymentDue: "",
  currencySymbol: "$",
  rows: [],
};

export default function InspectorPage() {
  const [submitted, setSubmitted] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(
    () => new URLSearchParams(window.location.search).get("projectId") ?? "",
  );
  const [currentDrawId, setCurrentDrawId] = useState("");
  const [currentProjectType, setCurrentProjectType] = useState<string>("");
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [sovItems, setSovItems] = useState<SovItem[]>([]);
  const [notDrawnItems, setNotDrawnItems] = useState<
    [string, string, string][]
  >([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [generalNotes, setGeneralNotes] = useState("");
  const [flagNotes, setFlagNotes] = useState("");
  const [offsiteNotes, setOffsiteNotes] = useState("");
  const [inspectorForm, setInspectorForm] = useState<InspectorForm>(EMPTY_FORM);
  const [drawData, setDrawData] = useState<DrawData>(EMPTY_DRAW_DATA);
  const [drawAmount, setDrawAmount] = useState("");
  const [drawPercentage, setDrawPercentage] = useState("");
  const [phases, setPhases] = useState<ResidentialPhase[]>([]);
  const [gcBilledPct, setGcBilledPct] = useState("");
  const [gcProgress, setGcProgress] = useState<GcProgressItem[]>([]);
  const [drawNarrative, setDrawNarrative] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [bankNotes, setBankNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);
  // hook to get projects list
  const {
    projects,
    loading: projectsLoading,
    refetch: refetchProjects,
  } = useInspectorProjects();
  // hook to get the commercial project inspection details
  const {
    inspectionData,
    loading: formLoading,
    error: formError,
  } = useInspectionCommercialForm(
    currentProjectType === "COMMERCIAL" ? currentProjectId : "",
    currentProjectType === "COMMERCIAL" ? currentDrawId : "",
  );
  // hook to get the residential project inspection details
  const { inspectionData: residentialData, loading: residentialFormLoading } =
    useInspectionResidentialForm(
      currentProjectType === "RESIDENTIAL" ? currentProjectId : "",
      currentProjectType === "RESIDENTIAL" ? currentDrawId : "",
    );

  useEffect(() => {
    if (projects.length === 0 || currentDrawId) return;
    const target = currentProjectId
      ? (projects.find((p) => p.id === currentProjectId) ?? projects[0])
      : projects[0];
    setCurrentProjectId(target.id);
    setCurrentDrawId(target.drawId ?? "");
    setCurrentProjectType(target.type);
    window.history.replaceState(null, "", `?projectId=${target.id}`);
  }, [projects, currentProjectId, currentDrawId]);

  useEffect(() => {
    if (!inspectionData) return;
    const storedName = (() => {
      try {
        const u = JSON.parse(localStorage.getItem("user") ?? "{}");
        return [u.firstName, u.lastName].filter(Boolean).join(" ");
      } catch {
        return "";
      }
    })();
    setCurrentProject(inspectionData.project);
    setSovItems(inspectionData.sovItems);
    setNotDrawnItems(inspectionData.notDrawnItems);
    const computedAvgPct = inspectionData.sovItems.length
      ? Math.round(
          inspectionData.sovItems.reduce((sum, item) => {
            const pct =
              item.customPct === ""
                ? (item.status ?? 0)
                : Number(item.customPct);
            return sum + (Number.isNaN(pct) ? 0 : pct);
          }, 0) / inspectionData.sovItems.length,
        )
      : 0;
    setInspectorForm({
      ...inspectionData.inspectorForm,
      name: storedName || inspectionData.inspectorForm.name,
      estimate:
        inspectionData.inspectorForm.estimate !== ""
          ? inspectionData.inspectorForm.estimate
          : String(computedAvgPct),
    });
    setGeneralNotes(inspectionData.generalNotes);
    setFlagNotes(inspectionData.flagNotes);
    setOffsiteNotes(inspectionData.offsiteNotes);
    setDrawData(inspectionData.drawData);
    setDrawAmount(inspectionData.drawAmount);
    setDrawPercentage(inspectionData.drawPercentage);
    setPhotos(inspectionData.photos);

    setSubmitted(false);
  }, [inspectionData]);

  useEffect(() => {
    if (!residentialData) return;
    const storedName = (() => {
      try {
        const u = JSON.parse(localStorage.getItem("user") ?? "{}");
        return [u.firstName, u.lastName].filter(Boolean).join(" ");
      } catch {
        return "";
      }
    })();
    setCurrentProject(residentialData.project);
    setPhases(residentialData.phases);
    setSovItems(residentialData.sovItems);
    setGcProgress(residentialData.gcProgress);
    setPhotos(residentialData.photos);
    setInspectorForm({
      ...residentialData.inspectorForm,
      name: storedName || residentialData.inspectorForm.name,
    });
    setGeneralNotes(residentialData.generalNotes);
    setFlagNotes(residentialData.flagNotes);
    setBankNotes(residentialData.bank);
    setDrawNarrative(residentialData.narrative);
    setScheduleNotes(residentialData.schedule);
    setDrawData(residentialData.drawData);
    setDrawAmount(residentialData.drawAmount);
    setDrawPercentage(residentialData.drawPercentage);
    setGcBilledPct(residentialData.drawPercentage);
    setSubmitted(false);
  }, [residentialData]);

  const metrics = useMemo<Metrics>(() => {
    const inspectedCount = sovItems.filter(
      (item) => item.customPct !== "" || item.status !== undefined,
    ).length;
    const flagsCount = sovItems.filter((item) => item.flagged).length;
    const avgPct = sovItems.length
      ? Math.round(
          sovItems.reduce((sum, item) => {
            const pct =
              item.customPct === "" ? item.status : Number(item.customPct);
            return sum + (Number.isNaN(pct) ? 0 : pct);
          }, 0) / sovItems.length,
        )
      : 0;
    return {
      inspectedCount,
      sovItemsCount: sovItems.length,
      flagsCount,
      avgPct,
      photosCount: photos.length,
    };
  }, [sovItems, photos.length]);

  const allResidentialItems = useMemo(
    () => phases.flatMap((p) => p.items),
    [phases],
  );

  const residentialMetrics = useMemo<Metrics>(() => {
    const inspectedCount = allResidentialItems.filter(
      (item) => item.customPct !== "" || item.status !== undefined,
    ).length;
    const flagsCount = allResidentialItems.filter(
      (item) => item.flagged,
    ).length;
    const avgPct = Math.round(
      allResidentialItems.reduce((sum, item) => {
        const pct =
          item.customPct !== "" ? Number(item.customPct) : (item.status ?? 0);
        return sum + (pct / 100) * item.weight;
      }, 0),
    );
    return {
      inspectedCount,
      sovItemsCount: allResidentialItems.length,
      flagsCount,
      avgPct,
      photosCount: photos.length,
    };
  }, [allResidentialItems, photos.length]);

  useEffect(() => {
    if (currentProjectType !== "COMMERCIAL") return;
    setInspectorForm((prev) => ({ ...prev, estimate: String(metrics.avgPct) }));
  }, [metrics.avgPct, currentProjectType]);

  useEffect(() => {
    if (currentProjectType !== "RESIDENTIAL") return;
    setInspectorForm((prev) => ({
      ...prev,
      estimate: String(residentialMetrics.avgPct),
    }));
  }, [residentialMetrics.avgPct, currentProjectType]);

  const setStatus = (id: string, status: number) => {
    setSovItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status, customPct: "" } : item,
      ),
    );
  };

  const setCustomPct = (id: string, rawValue: string) => {
    const numeric =
      rawValue === "" ? "" : Math.max(0, Math.min(100, Number(rawValue)));
    setSovItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, customPct: numeric } : item,
      ),
    );
  };

  const clearCustomPct = (id: string) => {
    setSovItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, customPct: "" } : item)),
    );
  };

  const toggleFlag = (id: string) => {
    setSovItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              flagged: !item.flagged,
              flagComment: "",
              extraComments: [],
            }
          : item,
      ),
    );
  };

  const setFlagComment = (id: string, text: string) => {
    setSovItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, flagged: true, flagComment: text } : item,
      ),
    );
  };

  const addExtraComment = (id: string, text: string) => {
    setSovItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, extraComments: [...(item.extraComments ?? []), text] }
          : item,
      ),
    );
  };

  const updateResidentialItem = (
    itemId: string,
    updater: (item: ResidentialLineItem) => ResidentialLineItem,
  ) => {
    setPhases((prev) =>
      prev.map((phase) => ({
        ...phase,
        items: phase.items.map((item) =>
          item.id === itemId ? updater(item) : item,
        ),
      })),
    );
  };

  const setResidentialStatus = (id: string, status: number) => {
    updateResidentialItem(id, (item) => ({ ...item, status, customPct: "" }));
  };

  const setResidentialCustomPct = (id: string, rawValue: string) => {
    const numeric =
      rawValue === "" ? "" : Math.max(0, Math.min(100, Number(rawValue)));
    updateResidentialItem(id, (item) => ({ ...item, customPct: numeric }));
  };

  const clearResidentialCustomPct = (id: string) => {
    updateResidentialItem(id, (item) => ({ ...item, customPct: "" }));
  };

  const toggleResidentialFlag = (id: string) => {
    updateResidentialItem(id, (item) => ({
      ...item,
      flagged: !item.flagged,
      flagComment: item.flagged ? "" : item.flagComment,
      extraComments: item.flagged ? [] : item.extraComments,
    }));
  };

  const setResidentialFlagComment = (id: string, text: string) => {
    updateResidentialItem(id, (item) => ({
      ...item,
      flagged: true,
      flagComment: text,
    }));
  };

  const addResidentialExtraComment = (id: string, text: string) => {
    updateResidentialItem(id, (item) => ({
      ...item,
      extraComments: [...(item.extraComments ?? []), text],
    }));
  };

  const addPhoto = (newPhotos: Photo[]) => {
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const updatePhotoCaption = (id: string, caption: string) => {
    setPhotos((prev) =>
      prev.map((photo) => (photo.id === id ? { ...photo, caption } : photo)),
    );
  };

  const handlePhotoUploaded = (id: string, s3Key: string) => {
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, s3Key, uploading: false } : p)),
    );
  };

  const handlePhotoUploadFailed = (id: string, error: string) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, uploading: false, uploadError: error } : p,
      ),
    );
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const buildPayload = (): InspectionFormPayload => ({
    inspectorName: inspectorForm.name,
    cscEstimation: metrics.avgPct,
    inspectionDate: today,
    note: { general: generalNotes, flags: flagNotes, offsite: offsiteNotes },
    lienItems: sovItems.map((item) => ({
      drawLienItemId: item.id,
      cscEstimation:
        item.customPct !== "" ? Number(item.customPct) : item.status,
      isFlagged: item.flagged,
      note: {
        ...(item.flagComment ? { flag: item.flagComment } : {}),
        ...(item.extraComments ?? []).reduce(
          (acc, c, i) => ({ ...acc, [String(i + 1)]: c }),
          {} as Record<string, string>,
        ),
      },
    })),
    documents: photos
      .filter((p) => p.s3Key)
      .map((p) => ({
        type: p.uploadType,
        document: { key: toS3Key(p.s3Key!), fileName: p.fileName },
        description: p.caption || null,
      })),
  });

  const handleSaveDraft = async () => {
    setIsSaving(true);
    setSaveError("");
    setSaveDraftSuccess(false);
    const { error } = await saveInspectionForm(
      currentProjectId,
      currentDrawId,
      buildPayload(),
      false,
    );
    setIsSaving(false);
    if (error) {
      setSaveError(getErrorMessage(error, "Failed to save draft."));
    } else {
      setSaveDraftSuccess(true);
      setTimeout(() => setSaveDraftSuccess(false), 3000);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    const { error } = await saveInspectionForm(
      currentProjectId,
      currentDrawId,
      buildPayload(),
      true,
    );
    setIsSubmitting(false);
    if (error) {
      setSubmitError(getErrorMessage(error, "Failed to submit inspection."));
    } else {
      setSubmitted(true);
      refetchProjects();
    }
  };

  const toS3Key = (key: string): string => {
    try {
      return new URL(key).pathname.replace(/^\//, "");
    } catch {
      return key;
    }
  };
  const today = getLocalToday();
  const buildResidentialPayload = (): ResidentialInspectionPayload => ({
    inspectorName: inspectorForm.name,
    inspectionDate: today,
    note: {
      general: generalNotes,
      flags: flagNotes,
      narrative: drawNarrative,
      schedule: scheduleNotes,
      bank: bankNotes,
    },
    fiItems: phases.flatMap((phase): ResidentialFiItemPayload[] => {
      const romanNumeral = phase.label.replace("Phase ", "").trim();
      return phase.items.map((item) => ({
        itemCode: item.id,
        phase: romanNumeral,
        cscEstimation:
          item.customPct !== "" ? Number(item.customPct) : (item.status ?? 0),
        isFlagged: item.flagged,
        note: {
          ...(item.flagComment ? { flag: item.flagComment } : {}),
          ...(item.extraComments ?? []).reduce(
            (acc, c, i) => ({ ...acc, [String(i + 1)]: c }),
            {} as Record<string, string>,
          ),
        },
      }));
    }),
    documents: photos
      .filter((p) => p.s3Key)
      .map((p) => ({
        type: p.uploadType,
        document: { key: toS3Key(p.s3Key!), fileName: p.fileName },
        description: p.caption || null,
      })),
  });

  const handleResidentialSaveDraft = async () => {
    setIsSaving(true);
    setSaveError("");
    setSaveDraftSuccess(false);
    const { error } = await saveResidentialInspectionForm(
      currentProjectId,
      currentDrawId,
      buildResidentialPayload(),
      false,
    );
    setIsSaving(false);
    if (error) {
      setSaveError(getErrorMessage(error, "Failed to save draft."));
    } else {
      setSaveDraftSuccess(true);
      setTimeout(() => setSaveDraftSuccess(false), 3000);
    }
  };

  const handleResidentialSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    const { error } = await saveResidentialInspectionForm(
      currentProjectId,
      currentDrawId,
      buildResidentialPayload(),
      true,
    );
    setIsSubmitting(false);
    if (error) {
      setSubmitError(getErrorMessage(error, "Failed to submit inspection."));
    } else {
      setSubmitted(true);
      refetchProjects();
    }
  };

  const openDirections = () => {
    if (!currentProject?.address) return;
    window.open(
      `https://maps.google.com/maps?daddr=${encodeURIComponent(currentProject.address)}`,
      "_blank",
    );
  };

  const handleInspectorFormChange = (
    field: keyof InspectorForm,
    value: string,
  ) => {
    setInspectorForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewInspection = async () => {
    setSubmitted(false);
    setCurrentProject(null);
    // Always await a fresh fetch — never rely on the potentially stale `projects` state.
    const freshProjects = await refetchProjects();
    const next = freshProjects[0] ?? null;
    if (next) {
      setCurrentProjectId(next.id);
      setCurrentDrawId(next.drawId ?? "");
      setCurrentProjectType(next.type ?? "");
      window.history.replaceState(null, "", `?projectId=${next.id}`);
    } else {
      // No pending inspections — clear everything so NoInspectionState renders.
      setCurrentProjectId("");
      setCurrentDrawId("");
      setCurrentProjectType("");
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  const handleProjectSelect = (id: string) => {
    if (id === currentProjectId) return;
    const selected = projects.find((p) => p.id === id);
    if (!selected) return;
    setCurrentProject(null);
    setCurrentProjectId(selected.id);
    setCurrentDrawId(selected.drawId ?? "");
    setCurrentProjectType(selected.type ?? "");
    window.history.replaceState(null, "", `?projectId=${selected.id}`);
  };

  const isLoading =
    projectsLoading ||
    (currentProjectId !== "" && (formLoading || residentialFormLoading));

  const displayProject: Project = currentProject ?? {
    id: "",
    name: "",
    draw: "",
    inspection: "",
    bank: "",
    type: "",
    address: "",
    sub: "",
    meta: "",
  };

  const sharedProps = {
    currentProject: displayProject,
    projects,
    currentProjectId,
    sovItems,
    notDrawnItems,
    photos,
    generalNotes,
    flagNotes,
    offsiteNotes,
    inspectorForm,
    drawData,
    drawAmount,
    drawPercentage,
    metrics,
    isSaving,
    isSubmitting,
    saveError,
    submitError,
    saveDraftSuccess,
    submitted,
    onSetStatus: setStatus,
    onSetCustomPct: setCustomPct,
    onClearCustomPct: clearCustomPct,
    onToggleFlag: toggleFlag,
    onFlagWithComment: setFlagComment,
    onAddComment: addExtraComment,
    onAddPhoto: addPhoto,
    onUpdateCaption: updatePhotoCaption,
    onPhotoUploaded: handlePhotoUploaded,
    onPhotoUploadFailed: handlePhotoUploadFailed,
    onRemovePhoto: removePhoto,
    onGeneralNotesChange: setGeneralNotes,
    onFlagNotesChange: setFlagNotes,
    onOffsiteNotesChange: setOffsiteNotes,
    onInspectorFormChange: handleInspectorFormChange,
    onSubmit: handleSubmit,
    onSaveDraft: handleSaveDraft,
    onNewInspection: handleNewInspection,
    onProjectSelect: handleProjectSelect,
    onOpenDirections: openDirections,
  };

  const residentialSharedProps = {
    currentProject: displayProject,
    projects,
    currentProjectId,
    phases,
    gcBilledPct,
    sovItems,
    gcProgress,
    photos,
    generalNotes,
    flagNotes,
    drawNarrative,
    scheduleNotes,
    bankNotes,
    inspectorForm,
    drawData,
    drawPercentage,
    metrics: residentialMetrics,
    isSaving,
    isSubmitting,
    saveError,
    submitError,
    saveDraftSuccess,
    submitted,
    onSetStatus: setResidentialStatus,
    onSetCustomPct: setResidentialCustomPct,
    onClearCustomPct: clearResidentialCustomPct,
    onToggleFlag: toggleResidentialFlag,
    onFlagWithComment: setResidentialFlagComment,
    onAddComment: addResidentialExtraComment,
    onAddPhoto: addPhoto,
    onUpdateCaption: updatePhotoCaption,
    onPhotoUploaded: handlePhotoUploaded,
    onPhotoUploadFailed: handlePhotoUploadFailed,
    onRemovePhoto: removePhoto,
    onGeneralNotesChange: setGeneralNotes,
    onFlagNotesChange: setFlagNotes,
    onDrawNarrativeChange: setDrawNarrative,
    onScheduleNotesChange: setScheduleNotes,
    onBankNotesChange: setBankNotes,
    onInspectorFormChange: handleInspectorFormChange,
    onSubmit: handleResidentialSubmit,
    onSaveDraft: handleResidentialSaveDraft,
    onNewInspection: handleNewInspection,
    onProjectSelect: handleProjectSelect,
    onOpenDirections: openDirections,
  };
  return (
    <>
      {/* ── Loading state ── */}
      {isLoading && !currentProject && (
        <div className="inspectorPage">
          <TopBar
            project={displayProject}
            onDirections={() => {}}
            onSwitch={() => {}}
          />
          <div className="inspectorContent">
            <div className="inspectorSkeletonTabs">
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="card"
                  width={52}
                  height={44}
                  borderRadius={8}
                />
              ))}
            </div>
            <div className="inspectorSkeletonContent">
              <Skeleton
                variant="text"
                width={`${100}%`}
                height={50}
                borderRadius={12}
              />
              <div className="inspectorSkeletonGrid">
                <Skeleton variant="card" height={72} borderRadius={10} />
                <Skeleton variant="card" height={72} borderRadius={10} />
                <Skeleton variant="card" height={72} borderRadius={10} />
                <Skeleton variant="card" height={72} borderRadius={10} />
              </div>
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={`${75 - i * 8}%`}
                  height={14}
                  borderRadius={4}
                />
              ))}
              <Skeleton variant="card" height={90} borderRadius={10} />
              {[...Array(4)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={`${80 - i * 6}%`}
                  height={14}
                  borderRadius={4}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Error state ── */}
      {formError && !isLoading && !currentProject && (
        <div className="inspectorPage">
          <TopBar
            project={displayProject}
            onDirections={() => {}}
            onSwitch={() => {}}
          />
          <div className="inspectorContent">
            <div className="inspectorError">{formError}</div>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && !formError && !currentProject && (
        <div className="inspectorPage">
          <TopBar
            project={displayProject}
            onDirections={() => {}}
            onSwitch={() => {}}
          />
          <div className="inspectorContent">
            <NoInspectionState />
          </div>
        </div>
      )}

      {/* ── Content states ── */}
      {currentProject &&
        (currentProjectType === "RESIDENTIAL" ? (
          <ResidentialInspectorPage {...residentialSharedProps} />
        ) : (
          <CommercialInspectorPage {...sharedProps} />
        ))}
    </>
  );
}
